import React, { useEffect, useMemo, useRef, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';

import { Input, Segmented, Tag } from 'antd';

import styled from 'styled-components';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useUserGuiConfig, useUserGuiConfigDispatch } from '@contexts/UserGuiConfigContext';

import CustomSwitch from '@components/Gui/CustomSwitch';
import MiniLoading from '@components/Gui/MiniLoading';
import { NwLinkButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

// Dentro un drawer lo spazio non manca: si lascia solo il posto per header e padding.
export const DRAWER_PANEL_MAX_HEIGHT = 'calc(100vh - 10rem)';

const PanelContainer = styled.div`
    display: flex;
    flex-direction: column;
    max-height: ${(props) => props.$maxHeight};
    min-height: 0;

    .tag-picker-search {
        padding: 0 0 0.75rem 0;
    }

    /* la modalita' del filtro: c'e' solo dove il pannello filtra una lista */
    .tag-picker-mode {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding-bottom: 0.75rem;

        > span {
            font-size: ${(props) => props.theme.FontSizeXSm};
            letter-spacing: 0.1em;
            text-transform: uppercase;
            color: ${(props) => props.theme.TextColorMuted};
        }
    }

    .tag-picker-selected {
        padding-bottom: 0.75rem;
        border-bottom: ${(props) => props.theme.LighterBorder};

        .selected-head {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 0.5rem;

            /* solo l'etichetta: il figlio diretto. Il link "clear all" tiene il suo stile */
            > span {
                font-size: ${(props) => props.theme.FontSizeXSm};
                letter-spacing: 0.1em;
                text-transform: uppercase;
                color: ${(props) => props.theme.TextColorMuted};
            }
        }

        .ant-tag {
            margin: 0 0.25rem 0.25rem 0;
            text-transform: lowercase;
        }
    }

    /* la scrollbar di perfect-scrollbar e' in overlay: non toglie larghezza alle righe,
       che restano a tutta la larghezza del box */
    .tag-picker-list {
        position: relative;
        flex: 1;
        min-height: 0;
        width: 100%;
        padding: 0.25rem 0;

        .ps__rail-y {
            /* perfect-scrollbar sceglie una volta sola, nel costruttore, se ancorare il rail a
               destra o a sinistra: legge il computed style e se non trova ancora applicato il
               CSS della libreria ripiega su left, che poi riscrive inline a ogni update.
               Serve !important perche' una regola normale non batte lo stile inline. */
            right: 0 !important;
            left: auto !important;
            opacity: 0;
            transition: opacity 0.2s;
        }

        &:hover .ps__rail-y,
        .ps__rail-y.ps--clicking {
            opacity: 0.6;
        }
    }

    .tag-picker-hints {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem 0.75rem;
        padding-top: 0.5rem;
        border-top: ${(props) => props.theme.LighterBorder};
        font-family: ${(props) => props.theme.FontFamilyMonospace};
        font-size: ${(props) => props.theme.FontSizeSm};
        color: ${(props) => props.theme.TextColorMuted};

        .tag-picker-hints-keys {
            display: flex;
            gap: 0.75rem;
            white-space: nowrap;
        }

        .tag-picker-hints-pref {
            cursor: pointer;
            white-space: nowrap;
            color: ${(props) => props.theme.TextColorMuted};

            /* il click e' sulla riga intera (testo incluso): lo switch e' solo indicatore */
            .ant-switch {
                pointer-events: none;
                margin-right: 0.4rem;
            }
        }

        kbd {
            font-family: inherit;
            border: ${(props) => props.theme.DarkerBorder};
            border-bottom-width: 2px;
            border-radius: 3px;
            padding: 0 0.35em;
            margin-right: 0.3em;
        }
    }
`;

const TagRow = styled.button`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    /* il canale a destra e' sempre riservato al rail (15px, ancorato al bordo): tenerlo fisso
       evita che i conteggi saltino quando la lista passa da scrollabile a non scrollabile */
    padding: 0.4rem 1rem 0.4rem 0.5rem;
    border: none;
    border-left: 2px solid transparent;
    background: none;
    text-align: left;
    cursor: pointer;
    color: ${(props) => props.theme.TextColor};

    &:hover,
    &.active {
        background-color: ${(props) => props.theme.LightBlueBackground};
        border-left-color: ${(props) => props.theme.PrimaryColor};
    }

    /* icona leggera a riposo, piena e primary quando la riga e' attiva o sotto il mouse:
       le due varianti sono entrambe nel DOM e si alternano via CSS */
    .mark {
        flex: none;
        display: flex;
        align-items: center;
        color: ${(props) => props.theme.TextColorMuted};

        .mark-solid {
            display: none;
        }
    }

    &:hover .mark,
    &.active .mark {
        color: ${(props) => props.theme.PrimaryColor};

        .mark-light {
            display: none;
        }

        .mark-solid {
            display: inline-block;
        }
    }

    .name {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: ${(props) => props.theme.FontSizeBase};
        /* i nomi conservano il case del catalogo: il minuscolo e' solo presentazione */
        text-transform: lowercase;

        em {
            font-style: normal;
            font-weight: bold;
        }

        small {
            margin-left: 0.5rem;
            font-size: ${(props) => props.theme.FontSizeXSm};
            letter-spacing: 0.1em;
            text-transform: uppercase;
            color: ${(props) => props.theme.TextColorMuted};
        }
    }

    .count {
        flex: none;
        font-family: ${(props) => props.theme.FontFamilyMonospace};
        font-size: ${(props) => props.theme.FontSizeXSm};
        font-variant-numeric: tabular-nums;
        color: ${(props) => props.theme.TextColorMuted};
    }

    &.create {
        color: ${(props) => props.theme.PrimaryColor};

        .mark {
            color: ${(props) => props.theme.PrimaryColor};
        }

        .name {
            font-weight: bold;
        }
    }
`;

// Le due varianti dell'icona di riga: la solid resta nascosta finche' la riga non e' attiva.
const RowMark = ({ icon }) => (
    <span className="mark">
        <NwIcon className="mark-light" icon={byPrefixAndName.fasl[icon]} />
        <NwIcon className="mark-solid" icon={byPrefixAndName.fass[icon]} />
    </span>
);

const EmptyRow = styled.p`
    margin: 0;
    padding: 1.5rem 0.5rem;
    text-align: center;
    font-size: ${(props) => props.theme.FontSizeSm};
    color: ${(props) => props.theme.TextColorMuted};
`;

// Evidenzia il tratto che ha fatto sopravvivere il tag alla ricerca.
// La posizione si cerca sul nome minuscolo, ma si taglia quello originale: stessa lunghezza.
const renderName = (name, query) => {
    if (!query) {
        return name;
    }
    const matchStart = name.toLowerCase().indexOf(query);
    if (matchStart < 0) {
        return name;
    }
    return (
        <>
            {name.slice(0, matchStart)}
            <em>{name.slice(matchStart, matchStart + query.length)}</em>
            {name.slice(matchStart + query.length)}
        </>
    );
};

/**
 * Pannello di selezione tag: ricerca, insieme selezionato, catalogo, creazione al volo.
 * Componente presentazionale: non legge il catalogo e non persiste i tag.
 * L'unica eccezione e' la preferenza "show always in drawer" (userGuiConfig), perche' il footer
 * degli shortcut e' l'unico punto condiviso fra popover, drawer e bulk picker.
 *
 * options   catalogo completo, [{ key, name, usageCount, isPrivate }]
 * selected  tag gia' applicati o filtri attivi, [{ key, name }]. Vengono mostrati come chip
 *           in cima e tolti dalla lista, che resta cosi' "quello che puoi ancora aggiungere".
 * onCreate  assente = riga di creazione nascosta (es. in modalita' filtro)
 */
const TagPickerPanel = ({
    options,
    selected,
    onToggle,
    onCreate,
    onClearAll,
    onRequestClose,
    selectedLabel = 'selected',
    // i tag si leggono sempre in primary, sia applicati a un oggetto sia come filtro attivo
    selectedColor = StyleVariables.PrimaryColor,
    searchPlaceholder,
    emptyMessage = 'No tags available',
    // icona a inizio riga: 'circle-plus' per aggiungere, 'circle-minus' nelle liste di sola rimozione
    rowIcon = 'circle-plus',
    maxHeight = '24rem',
    loading,
    // solo il filtro di una lista le passa: altrove la riga della modalita' non compare
    filterMode,
    onFilterModeChange,
}) => {
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const listRef = useRef();
    const searchRef = useRef();
    const { isMobile } = useLayoutContext();
    const userGuiConfig = useUserGuiConfig();
    const ugc_dispatch = useUserGuiConfigDispatch();
    const preferDrawer = Boolean(userGuiConfig && userGuiConfig.other && userGuiConfig.other.openTagPickerInDrawer);

    // ricerca e confronti sempre a minuscole: i nomi del catalogo conservano il loro case
    const normalizedQuery = query.trim().toLowerCase();

    const visibleOptions = useMemo(() => {
        const selectedKeys = selected.map((item) => item.key);
        return options.filter(
            (option) =>
                !selectedKeys.includes(option.key) &&
                (!normalizedQuery || option.name.toLowerCase().includes(normalizedQuery))
        );
    }, [options, selected, normalizedQuery]);

    // il confronto guarda tutto il catalogo, non solo i tag visibili: se il nome digitato
    // esiste gia' ed e' solo applicato, non ha senso proporre di crearlo di nuovo
    const canCreate =
        Boolean(onCreate) &&
        normalizedQuery.length > 0 &&
        !options.some((option) => option.name.toLowerCase() === normalizedQuery);

    const rowsCount = visibleOptions.length + (canCreate ? 1 : 0);

    // autoFocus non e' affidabile dentro il Popover, che monta il contenuto prima di
    // posizionarlo: si mette a fuoco il campo al frame successivo
    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            if (searchRef.current) {
                searchRef.current.focus();
            }
        });
        return () => cancelAnimationFrame(frame);
    }, []);

    useEffect(() => {
        setActiveIndex(0);
    }, [normalizedQuery]);

    useEffect(() => {
        // la riga si cerca per classe e non per indice: perfect-scrollbar aggiunge i propri
        // rail come figli del contenitore, quindi children[i] non corrisponde alla riga i
        const activeRow = listRef.current && listRef.current.querySelector('.tag-picker-row.active');
        if (activeRow) {
            activeRow.scrollIntoView({ block: 'nearest' });
        }
    }, [activeIndex]);

    // il click su una riga sposterebbe il focus sul suo button e le frecce smetterebbero
    // di funzionare: il focus resta sul campo di ricerca
    const keepFocusOnSearch = (event) => {
        event.preventDefault();
    };

    const handleToggle = (option) => {
        onToggle(option);
        setActiveIndex(0);
    };

    const handleCreate = () => {
        onCreate(query.trim());
        setQuery('');
    };

    const commitActiveRow = () => {
        if (canCreate && activeIndex === visibleOptions.length) {
            handleCreate();
        } else if (visibleOptions[activeIndex]) {
            handleToggle(visibleOptions[activeIndex]);
        }
    };

    const handleKeyDown = (event) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setActiveIndex((prev) => (rowsCount ? (prev + 1) % rowsCount : 0));
                break;
            case 'ArrowUp':
                event.preventDefault();
                setActiveIndex((prev) => (rowsCount ? (prev - 1 + rowsCount) % rowsCount : 0));
                break;
            case 'Enter':
                event.preventDefault();
                commitActiveRow();
                break;
            case 'Backspace':
                // campo vuoto: il backspace toglie l'ultimo tag selezionato
                if (!query && selected.length) {
                    onToggle(selected[selected.length - 1]);
                }
                break;
            case 'Escape':
                if (onRequestClose) {
                    event.preventDefault();
                    onRequestClose();
                }
                break;
            default:
                break;
        }
    };

    const handleTogglePreferDrawer = () => {
        ugc_dispatch({
            type: 'Update',
            value: {
                ...userGuiConfig,
                other: {
                    ...userGuiConfig.other,
                    openTagPickerInDrawer: !preferDrawer,
                },
            },
        });
    };

    return (
        <PanelContainer $maxHeight={maxHeight}>
            <div className="tag-picker-search">
                <Input
                    ref={searchRef}
                    autoComplete="off"
                    allowClear
                    placeholder={searchPlaceholder || (onCreate ? 'Search or create a tag' : 'Search tags')}
                    prefix={<NwIcon icon={byPrefixAndName.fasl['search']} />}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={handleKeyDown}
                />
            </div>
            {onFilterModeChange && (
                <div className="tag-picker-mode">
                    <span>match</span>
                    <Segmented
                        size="small"
                        value={filterMode}
                        onChange={onFilterModeChange}
                        options={[
                            { label: 'any', value: 'or' },
                            { label: 'all', value: 'and' },
                        ]}
                    />
                </div>
            )}
            {selected.length > 0 && (
                <div className="tag-picker-selected">
                    <div className="selected-head">
                        <span>{selectedLabel}</span>
                        {onClearAll && <NwLinkButton size="small" label="clear all" onClick={onClearAll} />}
                    </div>
                    <div>
                        {selected.map((item) => (
                            <Tag key={item.key} closable color={selectedColor} onClose={() => onToggle(item)}>
                                {item.name}
                            </Tag>
                        ))}
                    </div>
                </div>
            )}
            {loading ? (
                <MiniLoading />
            ) : (
                <PerfectScrollbar
                    className="tag-picker-list"
                    // wheelPropagation di default e' true: arrivati a fine lista la rotella
                    // scorrerebbe la pagina o il drawer dietro al pannello
                    options={{ suppressScrollX: true, wheelPropagation: false }}
                    containerRef={(container) => {
                        listRef.current = container;
                    }}
                >
                    {visibleOptions.map((option, index) => (
                        <TagRow
                            key={option.key}
                            type="button"
                            className={`tag-picker-row${index === activeIndex ? ' active' : ''}`}
                            onClick={() => handleToggle(option)}
                            onMouseDown={keepFocusOnSearch}
                            onMouseEnter={() => setActiveIndex(index)}
                        >
                            <RowMark icon={rowIcon} />
                            <span className="name">
                                {renderName(option.name, normalizedQuery)}
                                {option.isPrivate && <small>private</small>}
                            </span>
                            {Number.isFinite(option.usageCount) && <span className="count">{option.usageCount}</span>}
                        </TagRow>
                    ))}
                    {canCreate && (
                        <TagRow
                            type="button"
                            className={`tag-picker-row create${activeIndex === visibleOptions.length ? ' active' : ''}`}
                            onClick={handleCreate}
                            onMouseDown={keepFocusOnSearch}
                            onMouseEnter={() => setActiveIndex(visibleOptions.length)}
                        >
                            <RowMark icon="circle-plus" />
                            <span className="name">create &quot;{query.trim()}&quot;</span>
                        </TagRow>
                    )}
                    {!visibleOptions.length && !canCreate && (
                        <EmptyRow>{normalizedQuery ? `No tag matches "${query.trim()}"` : emptyMessage}</EmptyRow>
                    )}
                </PerfectScrollbar>
            )}
            {!isMobile && (
                <div className="tag-picker-hints">
                    <div className="tag-picker-hints-keys">
                        <span>
                            <kbd>↑↓</kbd>move
                        </span>
                        <span>
                            <kbd>⏎</kbd>
                            {onCreate ? 'add' : 'apply'}
                        </span>
                        <span>
                            <kbd>esc</kbd>close
                        </span>
                    </div>
                    <span
                        className="tag-picker-hints-pref"
                        role="switch"
                        tabIndex={0}
                        aria-checked={preferDrawer}
                        title={
                            preferDrawer
                                ? 'Tag selection opens in a drawer. Click to use a popover instead.'
                                : 'Tag selection opens in a popover. Click to always use a drawer.'
                        }
                        onClick={handleTogglePreferDrawer}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleTogglePreferDrawer();
                            }
                        }}
                    >
                        <CustomSwitch size="small" tabIndex={-1} value={preferDrawer} text="show always in drawer" />
                    </span>
                </div>
            )}
        </PanelContainer>
    );
};

export default TagPickerPanel;
